import { action } from "@ember/object"
import { inject as service } from "@ember/service"
import { htmlSafe } from "@ember/template"
import Component from "@glimmer/component"
import { track } from "cc-frontend/services/callbacks/action/analytics"
import type Session from "cc-frontend/services/session"
import { task, timeout } from "ember-concurrency"
import { tracked } from "tracked-built-ins"
import styles from "./base.module.css"

interface Args {
  videoId: string
  isMobile: boolean
}

export default class OnboardingVideoBase extends Component<Args> {
  readonly styles = styles

  @service declare session: Session

  video: any = null
  @tracked videoIsDone = false
  @tracked videoHasLoaded = true
  @tracked videoWatchCount = 0
  @tracked hasWatchedEvents = false
  @tracked canShow = false

  desktopStyleString = htmlSafe(
    `margin-left: 5vh; margin-right: 5vh; max-width: calc(1.7 * 80vh); max-height: 80vh; width: 96vw; height: calc(.5625 * 96vw); position: relative;`
  )
  mobileStyleString = htmlSafe(
    `margin-left: 5vh; margin-right: 5vh; max-width: calc(.5625 * 80vh); max-height: 80vh; width: 96vw; height: calc(1.7 * 96vw); position: relative;`
  )

  @action ensureVideo() {
    this.runVideo.perform()
  }

  runVideo = task(async () => {
    await this.autoLoadVideo.perform()
    await this.autoPlayVideo.perform()
  })

  autoLoadVideo = task(async () => {
    console.log("BEGIN: Autoload loop")
    let iteration = 0

    while (true) {
      console.log("Autoload loop iteration:", iteration++)
      let hasLoaded = await this.loadVideo.perform()
      console.log("Video has loaded?", hasLoaded)
      if (hasLoaded === true) {
        return
      } else {
        await timeout(25)
      }
    }
  })

  loadVideo = task({ keepLatest: true }, async () => {
    if (typeof Wistia === "undefined") {
      console.log("Wistia undefined")
      return false
    }

    try {
      Wistia.options(this.args.videoId, {
        autoPlay: true,
      })
      let video = Wistia.api(this.args.videoId)
      if (video === null) {
        console.log("There is not a video")
        return false
      } else {
        console.log("There is a Video")
        this.video = video
        return this.video.ready()
      }
    } catch (e) {
      console.log("Error loading video", e)
      return false
    }
  })

  autoPlayVideo = task(async () => {
    this.videoHasLoaded = true
    this.video.bind("end", (e) => this.onVideoEnded())
    this.video.bind("play", (e) => this.onVideoStarted())
    console.log("BEGIN: Autoplay video")
    let iteration = 0

    while (true) {
      console.log("autoplay iteration:", iteration++)
      let isPlaying = await this.startVideo.perform()
      if (isPlaying === true) {
        return
      } else {
        await timeout(2000)
      }
    }
  })

  startVideo = task(async () => {
    console.log("Play video", this.video.state())
    this.video.play()
    return this.video.state() === "playing"
  })

  onVideoStarted() {
    this.videoWatchCount++
    console.log("Video starts")
    if (this.videoWatchCount === 1) {
      track(this.session, `Onboarding Video - Started`, { videoId: this.args.videoId })
    } else {
      track(this.session, `Onboarding Video - Rewatch`, { videoId: this.args.videoId })
    }
  }

  onVideoEnded() {
    this.videoIsDone = true
    track(this.session, `Onboarding Video - Finished`, { videoId: this.args.videoId })
  }

  @action
  destroyVideo() {
    if (typeof Wistia === "undefined") return
    try {
      let api = Wistia.api(this.args.videoId)
      if (api) api.remove()
    } catch (e) {
      console.log("Error removing video", e)
    }
  }

  @action rewatch() {
    if (typeof Wistia == "undefined") return
    this.videoIsDone = false
    try {
      let player = Wistia.api(this.args.videoId)
      player.play()
    } catch (e) {
      console.log("Error playing video", e)
    }
  }
}
